import { looksLikeOpaqueUserId } from "@sd/lib/ticketRowMapping";
import {
  extractPayloadArray,
  firstNonEmptyString,
} from "@sd/lib/userDisplayMap";

export type RequesterFilterOption = {
  id: string;
  name: string;
  matchIds: string[];
};

type RequesterFilterRow = RequesterFilterOption & {
  isUserId: boolean;
};

function parseEmployeeListForRequesterFilter(payload: unknown): RequesterFilterRow[] {
  const out: RequesterFilterRow[] = [];

  for (const raw of extractPayloadArray(payload)) {
    if (!raw || typeof raw !== "object") continue;
    const o = raw as Record<string, unknown>;

    const userId = String(o.user_id ?? o.django_user_id ?? "").trim();
    const recordId = String(
      o.id ?? o.pk ?? o.employee_id ?? o.agent_id ?? ""
    ).trim();
    const filterId = userId || recordId;
    if (!filterId) continue;

    const fullName = [
      o.firstname ?? o.first_name ?? o.firstName,
      o.lastname ?? o.last_name ?? o.lastName,
    ]
      .filter(Boolean)
      .map(String)
      .join(" ")
      .trim();

    const name = firstNonEmptyString(
      fullName || undefined,
      o.fullname,
      o.full_name,
      o.fullName,
      o.name,
      o.display_name,
      o.displayName,
      o.email,
      o.username
    );

    if (!name || looksLikeOpaqueUserId(name)) continue;

    const matchIds = [...new Set([filterId, userId, recordId].filter(Boolean))];
    out.push({
      id: filterId,
      name,
      isUserId: Boolean(userId),
      matchIds,
    });
  }

  return out;
}

function looksLikeRawIdLabel(s: string): boolean {
  const t = s.trim();
  if (looksLikeOpaqueUserId(t)) return true;
  if (/^[a-f0-9]{8,32}$/i.test(t)) return true;
  return false;
}

function nameKey(name: string): string {
  return name.trim().toLowerCase();
}

/** Build requester filter options from employee lookup APIs (one row per person). */
export function mergeRequesterFilterOptions(
  ...payloads: unknown[]
): RequesterFilterOption[] {
  const byName = new Map<string, RequesterFilterRow>();

  for (const payload of payloads) {
    for (const row of parseEmployeeListForRequesterFilter(payload)) {
      const key = nameKey(row.name);
      const existing = byName.get(key);
      if (!existing) {
        byName.set(key, row);
        continue;
      }

      const mergedMatchIds = [...new Set([...existing.matchIds, ...row.matchIds])];
      const preferRow =
        (row.isUserId && !existing.isUserId) ||
        (row.isUserId === existing.isUserId && row.id.length >= existing.id.length);

      byName.set(key, {
        id: preferRow ? row.id : existing.id,
        name: existing.name,
        isUserId: existing.isUserId || row.isUserId,
        matchIds: mergedMatchIds,
      });
    }
  }

  return [...byName.values()]
    .filter(row => !looksLikeOpaqueUserId(row.name))
    .map(row => ({ id: row.id, name: row.name, matchIds: row.matchIds }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Assign-employees dropdown from GET `/service_desk/employee_list/`.
 * Labels match Admin Employees (`firstname` + `lastname`); value is employee id
 * for `assign_employees`.
 */
export function employeeListAssignSelectOptions(
  payload: unknown
): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  const seen = new Set<string>();

  for (const raw of extractPayloadArray(payload)) {
    if (!raw || typeof raw !== "object") continue;
    const o = raw as Record<string, unknown>;
    const id = String(
      o.id ?? o.pk ?? o.employee_id ?? o.user_id ?? ""
    ).trim();
    if (!id) continue;

    const fullName = [
      o.firstname ?? o.first_name ?? o.firstName,
      o.lastname ?? o.last_name ?? o.lastName,
    ]
      .filter(Boolean)
      .map(String)
      .join(" ")
      .trim();

    const name = firstNonEmptyString(
      ...[
        fullName || undefined,
        o.fullname,
        o.full_name,
        o.fullName,
        o.employee_name,
        o.display_name,
        o.displayName,
        o.name,
        o.email,
        o.emailaddress,
        o.username,
      ].filter(v => !looksLikeRawIdLabel(String(v ?? "")))
    );
    if (!name) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ value: id, label: name });
  }

  return out.sort((a, b) => a.label.localeCompare(b.label));
}

export function matchesRequesterFilter(
  ticketRequester: string | null | undefined,
  filterId: string,
  options: RequesterFilterOption[]
): boolean {
  if (!filterId || filterId === "all") return true;
  const raw = String(ticketRequester ?? "").trim();
  if (!raw) return false;

  const opt = options.find(o => o.id === filterId);
  if (opt?.matchIds.includes(raw)) return true;
  if (opt && raw.toLowerCase() === opt.name.toLowerCase()) return true;
  return false;
}
